import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import NavHeader from './NavProjects';
import './Projects.css';

// Container for the entire page
const PageContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

// Background canvas for Matrix rain effect
const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

// Main content container
const ProjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  z-index: 1;
  width: 95%;
  max-width: 1400px;
  margin: 2rem auto;
  min-height: 80vh;

  @media (max-width: 768px) {
    width: 95%;
    padding: 1rem;
    margin: 1rem;
  }
`;

// Grid layout for project cards
const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: clamp(1rem, 4vw, 2rem);
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Enhanced glow and flicker animations
const glowAnimation = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
  50% { box-shadow: 0 0 15px rgba(0, 255, 0, 0.5); }
  100% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
`;

const flickerAnimation = keyframes`
  0% { opacity: 0.9; }
  5% { opacity: 1; }
  10% { opacity: 0.9; }
  15% { opacity: 1; }
  70% { opacity: 1; }
  80% { opacity: 0.8; }
  90% { opacity: 1; }
  100% { opacity: 1; }
`;

// Enhanced project card with expansion on hover
const ProjectCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: rgba(0, 10, 0, 0.95);
  border: 1px solid #00ff00;
  border-radius: 8px;
  padding: clamp(1rem, 3vw, 1.75rem);
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${glowAnimation} 3s infinite;
  height: auto;
  min-height: 400px;
  z-index: 1;
  
  &:hover, &:focus-within {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 0 25px rgba(0, 255, 0, 0.7);
    z-index: 2;
  }
  
  &:focus-visible {
    outline: 2px dashed #00ff00;
    outline-offset: 3px;
  }
  
  &::before {
    content: '$ cat project';
    position: absolute;
    top: 10px;
    left: 10px;
    font-size: 0.8rem;
    color: #00ff00;
    opacity: 0.7;
  }
`;

// Project title styling
const ProjectTitle = styled.h3`
  color: #00ff99;
  margin: 1.5rem 0 0.5rem;
  font-family: 'Courier New', monospace;
  border-bottom: 1px solid #00ff00;
  padding-bottom: 0.5rem;
  width: 100%;
  font-size: clamp(1.1rem, 3vw, 1.3rem);
`;

// Short description that will be scrambled
const ProjectDescription = styled.p`
  color: #00ff00;
  margin: 0.5rem 0 1rem;
  font-family: 'Courier New', monospace;
  min-height: 60px;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
`;

// Category tags container
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 1rem;
`;

// Individual category tag
const Tag = styled.span`
  background-color: rgba(0, 50, 0, 0.7);
  border: 1px solid #00ff00;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #00ff00;
`;

// Project thumbnail
const ProjectImage = styled.img`
  width: 100%;
  height: auto;
  min-height: 150px;
  max-height: 200px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #00ff00;
`;

// Accessibility toggle
const AccessibilityToggle = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: rgba(0, 20, 0, 0.8);
  border: 1px solid #00ff00;
  border-radius: 4px;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  z-index: 100;
  
  label {
    margin-left: 0.5rem;
    color: #00ff00;
    cursor: pointer;
  }
`;

// New components for enhanced functionality

// Tech stack display that appears on hover
const TechStack = styled.div`
  margin: 0.5rem 0;
  height: ${props => props.isHovered ? 'auto' : '0'};
  opacity: ${props => props.isHovered ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  
  span {
    display: inline-block;
    background-color: rgba(0, 30, 0, 0.8);
    border-radius: 3px;
    padding: 2px 5px;
    margin-right: 5px;
    margin-bottom: 5px;
    color: #00dd00;
    border: 1px solid rgba(0, 255, 0, 0.3);
  }
`;

// Quick stat that appears on hover
const QuickStat = styled.div`
  margin-top: 0.5rem;
  font-weight: bold;
  color: #00ffaa;
  opacity: ${props => props.isHovered ? '1' : '0'};
  height: ${props => props.isHovered ? 'auto' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

// Interactive buttons container
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  opacity: ${props => props.isHovered ? '1' : '0'};
  transform: translateY(${props => props.isHovered ? '0' : '10px'});
  transition: all 0.3s ease;
`;

// Individual terminal-styled button
const TerminalButton = styled.a`
  background-color: rgba(0, 20, 0, 0.8);
  color: #00ff00;
  border: 1px solid #00ff00;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  text-decoration: none;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  animation: ${flickerAnimation} 2s infinite;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background-color: rgba(0, 40, 0, 0.9);
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  }
  
  &:focus {
    outline: none;
  }
`;

// Media preview that appears on hover/click
const MediaPreview = styled.div`
  position: absolute;
  top: -180px;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 90%;
  height: 160px;
  background-color: rgba(0, 10, 0, 0.95);
  border: 1px solid #00ff00;
  border-radius: 8px;
  overflow: hidden;
  opacity: ${props => props.isVisible ? '1' : '0'};
  transform: translateY(${props => props.isVisible ? '0' : '20px'});
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  transition: all 0.3s ease;
  z-index: 3;
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.4);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: 'Full Details →';
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: rgba(0, 20, 0, 0.8);
    color: #00ff00;
    padding: 0.2rem 0.5rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 768px) {
    top: -120px;
    height: 100px;
  }
  
  @media (max-width: 480px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) ${props => props.isVisible ? 'scale(1)' : 'scale(0.8)'};
    opacity: ${props => props.isVisible ? '1' : '0'};
    width: 80%;
    height: auto;
    z-index: 1000;
  }
`;

// Progress bar for WIP projects
const ProgressContainer = styled.div`
  margin-top: 0.5rem;
  opacity: ${props => props.isHovered ? '1' : '0.7'};
  transition: opacity 0.3s ease;
  font-size: 0.8rem;
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  background-color: rgba(0, 20, 0, 0.5);
  border: 1px solid #00ff00;
  margin-top: 4px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => `${props.progress}%`};
    height: 100%;
    background-color: rgba(0, 255, 0, 0.3);
    background-image: linear-gradient(
      45deg,
      rgba(0, 255, 0, 0.5) 25%,
      transparent 25%,
      transparent 50%,
      rgba(0, 255, 0, 0.5) 50%,
      rgba(0, 255, 0, 0.5) 75%,
      transparent 75%
    );
    background-size: 16px 16px;
    animation: ${props => props.isHovered ? css`progress-animation 1s linear infinite` : 'none'};
  }
  
  &::after {
    content: '[${props => '='.repeat(Math.floor(props.progress / 10)) + ' '.repeat(10 - Math.floor(props.progress / 10))}] ${props => props.progress}%';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #00ff00;
    font-family: 'Courier New', monospace;
    font-size: 0.7rem;
  }
`;

// Text Scramble Effect Component
class TextScramble extends React.Component {
  constructor(props) {
    super(props);
    this.chars = "!<>-_\\/[]{}—=+*^?#________";
    this.queue = [];
    this.frame = 0;
    this.frameRequest = null;
    this.update = this.update.bind(this);
    this.state = { text: "" };
  }

  componentDidMount() {
    this.setText(this.props.text);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.text !== this.props.text || prevProps.triggerScramble !== this.props.triggerScramble) {
      this.setText(this.props.text);
    }
  }

  setText(newText) {
    const oldText = this.state.text;
    const length = Math.max(oldText.length, newText.length);
    const queue = [];
    for (let i = 0; i < length; i++) {
      const from = oldText[i] || "";
      const to = newText[i] || "";
      const start = Math.floor(Math.random() * 40);
      const end = start + Math.floor(Math.random() * 40);
      queue.push({ from, to, start, end });
    }
    this.queue = queue;
    cancelAnimationFrame(this.frameRequest);
    this.frame = 0;
    this.update();
  }

  update() {
    let output = "";
    let complete = 0;

    for (let i = 0, n = this.queue.length; i < n; i++) {
      let { from, to, start, end, char } = this.queue[i];
      if (this.frame >= end) {
        complete++;
        output += to;
      } else if (this.frame >= start) {
        if (!char || Math.random() < 0.28) {
          char = this.chars[Math.floor(Math.random() * this.chars.length)];
          this.queue[i].char = char;
        }
        output += `<span>${char}</span>`;
      } else {
        output += from;
      }
    }

    this.setState({ text: output });
    if (complete === this.queue.length) {
      cancelAnimationFrame(this.frameRequest);
    } else {
      this.frame++;
      this.frameRequest = requestAnimationFrame(this.update);
    }
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.frameRequest);
  }

  render() {
    return <div dangerouslySetInnerHTML={{ __html: this.state.text }} />;
  }
}

const Projects = () => {
  const canvasRef = useRef(null);
  const [hoverStates, setHoverStates] = useState({});
  const [previewStates, setPreviewStates] = useState({});
  const [disableAnimations, setDisableAnimations] = useState(false);
  
  // Enhanced project data with additional fields
  const projects = [
    {
      id: 1,
      title: "Gesture Recognition System",
      shortDescription: "Motion-based gesture detection with DTW algorithm",
      hoverDescription: "STM32 gyroscope with Dynamic Time Warping algorithm",
      categories: ["Embedded Systems", "Signal Processing"],
      techStack: ["STM32", "SPI", "mbed", "C++", "DTW"],
      mediaUrl: "Gesture/Gest.jpeg",
      quickStat: "Recognition rate: 96%",
      // links: {
      //   view: "/project/gesture",
      //   code: "https://github.com/username/gesture-recognition"
      // },
      progress: 100  // 100% complete
    },
    {
      id: 2,
      title: "CV Product Size Estimation",
      shortDescription: "Computer vision-based product size estimation",
      hoverDescription: "YOLOv8 and Stereovision for reference-free size estimation",
      categories: ["Computer Vision", "IoT", "3D Reconstruction"],
      techStack: ["Python", "YOLOv8", "OpenCV", "Stereovision", "Depth Mapping"],
      mediaUrl: "/CV Size/Size.jpg",
      quickStat: "Accurate size estimation without reference objects",
      // links: {
      //   view: "/project/smart-vending",
      //   code: "https://github.com/username/smart-vending"
      // },
      progress: 100,
      description: "Pioneered the integration of a novel computer vision system using YOLOv8, OpenCV, and Stereovision technologies, enabling accurate estimation of product sizes at distance, without the need for reference objects."
    },
    {
      id: 3,
      title: "Event Reservation System",
      shortDescription: "Dual-interface flight booking platform",
      hoverDescription: "Web app with admin and customer portals for flight bookings",
      categories: ["Web Application", "Backend"],
      techStack: ["HTML", "CSS", "Flask", "SQL", "Python"],
      mediaUrl: "/web app/Homepage.png",
      quickStat: "Handled 3000+ bookings",
      // links: {
      //   view: "/project/reservation",
      //   code: "https://github.com/username/reservation-system"
      // },
      progress: 100
    },
    {
      id: 4,
      title: "ML Brest Cancer Prediction",
      shortDescription: "Machine learning for medical diagnosis",
      hoverDescription: "91.3% accuracy in predicting breast cancer survivability",
      categories: ["Machine Learning", "Healthcare"],
      techStack: ["Python", "NumPy", "Pandas", "Scikit-learn", "TensorFlow"],
      mediaUrl: "/ML/NN.png",
      quickStat: "Accuracy: 91.3%",
      // links: {
      //   view: "/project/cancer-ml",
      //   code: "https://github.com/username/cancer-prediction"
      // },
      progress: 100
    },
    {
      id: 5,
      title: "Blood Pressure Monitor",
      shortDescription: "Integrated health monitoring device",
      hoverDescription: "ATmega2514 microcontroller with pressure sensor integration",
      categories: ["Embedded Systems", "Healthcare"],
      techStack: ["ATmega2514", "I2C", "Arduino", "C++"],
      mediaUrl: "/pressure/BloodPressureSet.jpg",
      quickStat: "Accuracy: ±2 mmHg",
      // links: {
      //   view: "/project/bp-monitor",
      //   code: "https://github.com/username/bp-monitor"
      // },
      progress: 100
    },
    {
      id: 6,
      title: "E20 Processor Simulator",
      shortDescription: "Comprehensive E20 assembly language toolchain",
      hoverDescription: "Assembler, simulator and cache implementation in C++",
      categories: ["Computer Architecture", "Systems"],
      techStack: ["Assembly", "C++", "Computer Architecture"],
      mediaUrl: "/E20/Archticture.png",
      quickStat: "99.8% instruction compatibility",
      // links: {
      //   view: "/project/e20-simulator",
      //   code: "https://github.com/username/e20-simulator"
      // },
      progress: 100
    },
    {
      id: 7,
      title: "Stero Vison Door Status Detection",
      shortDescription: "Computer vision-based security monitoring",
      hoverDescription: "Depth-based stereo vision for efficient door state detection",
      categories: ["Computer Vision", "Security Systems", "IoT"],
      techStack: ["Python", "OpenCV", "Stereo Vision", "Depth Mapping"],
      mediaUrl: "/door/depth.png",
      quickStat: "Accuracy: +-1cm",
      // links: {
      //   view: "/project/door-detection",
      //   code: "https://github.com/username/door-detection"
      // },
      progress: 100,
      description: "Developed a computer vision-based door status detection algorithm using depth-based stereo vision techniques to reduce hardware costs and improve system efficiency and security."
    }
  ];

  // Matrix background effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
    };

    handleResize();
    
    const columns = Math.floor(window.innerWidth / 10);
    const drops = Array(columns).fill(1);

    const draw = () => {
        context.fillStyle = 'rgba(0, 0, 0, 0.05)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#00ff00';
        context.font = '10px monospace';

        drops.forEach((y, index) => {
            const text = String.fromCharCode(Math.floor(Math.random() * 128));
            const x = index * 10;
            context.fillText(text, x, y * 10);

            if (y * 10 > canvas.height && Math.random() > 0.975) {
                drops[index] = 0;
            }
            drops[index]++;
        });
    };

    window.addEventListener('resize', handleResize);
    
    const interval = setInterval(draw, 50);

    return () => {
        clearInterval(interval);
        window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Enhanced handle card hover to include button and preview visibility
  const handleMouseEnter = (id) => {
    if (!disableAnimations) {
      setHoverStates(prev => ({
        ...prev,
        [id]: true
      }));
    }
  };

  const handleMouseLeave = (id) => {
    setHoverStates(prev => ({
      ...prev,
      [id]: false
    }));
    
    // Optional: Also hide the preview when leaving the card
    setPreviewStates(prev => ({
      ...prev,
      [id]: false
    }));
  };

  // Handle showing preview on card thumbnail click
  const handleImageClick = (e, id) => {
    e.stopPropagation(); // Prevent triggering card click
    setPreviewStates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Navigate to project details
  const handleCardClick = (id) => {
    console.log(`Navigating to project ${id}`);
    window.location.href = `/project/${id}`;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(id);
    }
  };

  // Toggle animations
  const toggleAnimations = () => {
    setDisableAnimations(!disableAnimations);
  };

  return (
    <PageContainer>
      <Canvas ref={canvasRef} />
      <NavHeader title="Projects" />
      <ProjectsContainer>
        <ProjectGrid>
          {projects.map((project) => (
            <ProjectCard 
              key={project.id}
              onMouseEnter={() => handleMouseEnter(project.id)}
              onMouseLeave={() => handleMouseLeave(project.id)}
              onClick={() => handleCardClick(project.id)}
              onKeyDown={(e) => handleKeyDown(e, project.id)}
              tabIndex="0"
              className="project-card-hover project-card"
            >
              {/* Media preview that shows on hover/click */}
              <MediaPreview 
                isVisible={previewStates[project.id]} 
                onClick={(e) => e.stopPropagation()}
              >
                <img src={project.mediaUrl || "/placeholder.jpg"} alt={`${project.title} preview`} />
              </MediaPreview>
              
              {/* Project thumbnail */}
              <ProjectImage 
                src={project.mediaUrl || "/placeholder.jpg"} 
                alt={project.title} 
                className="project-image"
                onClick={(e) => handleImageClick(e, project.id)}
              />
              
              {/* Project title */}
              <ProjectTitle>{project.title}</ProjectTitle>
              
              {/* Description with scramble effect */}
              <ProjectDescription>
                {disableAnimations ? (
                  <div>{project.shortDescription}</div>
                ) : (
                  <TextScramble 
                    text={hoverStates[project.id] ? project.hoverDescription : project.shortDescription}
                    triggerScramble={hoverStates[project.id] ? 1 : 0}
                    className="term-text"
                  />
                )}
              </ProjectDescription>
              
              {/* Tech stack that appears on hover */}
              <TechStack isHovered={hoverStates[project.id]}>
                {project.techStack.map((tech, index) => (
                  <span key={index} className="project-tag">{tech}</span>
                ))}
              </TechStack>
              
              {/* Quick stat that appears on hover */}
              <QuickStat isHovered={hoverStates[project.id]}>
                {project.quickStat}
              </QuickStat>
              
              {/* Progress bar for WIP projects */}
              {project.progress < 100 && (
                <ProgressContainer isHovered={hoverStates[project.id]}>
                  <div>Project Status:</div>
                  <ProgressBar 
                    progress={project.progress} 
                    isHovered={hoverStates[project.id]}
                  />
                </ProgressContainer>
              )}
              
              {/* Category tags */}
              <TagsContainer>
                {project.categories.map((category, index) => (
                  <Tag key={index} className="project-tag">{category}</Tag>
                ))}
              </TagsContainer>
              
              {/* Interactive buttons - only show DEMO button */}
              <ButtonContainer isHovered={hoverStates[project.id]}>
                {project.links && project.links.demo && (
                  <TerminalButton 
                    href={project.links.demo} 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={hoverStates[project.id] ? "0" : "-1"}
                  >
                    [DEMO]
                  </TerminalButton>
                )}
              </ButtonContainer>
            </ProjectCard>
          ))}
        </ProjectGrid>
      </ProjectsContainer>
      
      <AccessibilityToggle onClick={toggleAnimations}>
        <input 
          type="checkbox"
          checked={disableAnimations}
          onChange={toggleAnimations}
          id="animation-toggle"
        />
        <label htmlFor="animation-toggle">
          {disableAnimations ? "Enable Animations" : "Disable Animations"}
        </label>
      </AccessibilityToggle>
    </PageContainer>
  );
};

export default Projects;

